import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Loader2, ChevronDown, FileText, FileDown } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import LicenceChecks from "@/components/LicenceChecks";
import { toast } from "sonner";

const SYNCED = ["last_check_date", "next_check_due", "licence_points", "licence_number", "licence_categories", "check_code", "licence_expiry"];

export default function HRDriverDetail() {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadDriver = () => api.get(`/hr/drivers/${id}`).then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e)));
  const refreshStats = () => api.get(`/hr/drivers/${id}`).then((r) => setForm((f) => ({ ...f, doc_stats_by_type: r.data.doc_stats_by_type })));
  const refreshFromChecks = () => api.get(`/hr/drivers/${id}`).then((r) => setForm((f) => ({ ...f, ...Object.fromEntries(SYNCED.map((k) => [k, r.data[k]])) })));

  useEffect(() => {
    loadDriver();
    api.get("/hr/doc-types").then((r) => setDocTypes(r.data || []));
  }, [id]); // eslint-disable-line

  if (!form) return <div className="text-sm text-slate-500">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/hr/drivers/${id}`, { ...form, licence_points: Number(form.licence_points) || 0 });
      setForm((f) => ({ ...r.data, doc_stats_by_type: f.doc_stats_by_type }));
      toast.success("Driver saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={`hr-field-${k}`} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-sm border-slate-300" {...props} />
    </div>
  );

  return (
    <div data-testid="hr-driver-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <Link to="/hr" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="hr-back"><ArrowLeft className="h-3 w-3" /> All drivers</Link>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2" data-testid="hr-driver-name">{form.name}</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="hr-driver-pdf-btn" variant="outline" className="rounded-sm"
            onClick={() => downloadPdf(`/hr/drivers/${id}/file.pdf`, `driver-file-${(form.name || "driver").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`)}>
            <FileDown className="h-4 w-4 mr-2" />Driver file PDF
          </Button>
          <Button data-testid="hr-save-btn" onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
          </Button>
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-5">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Driver details</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {field("name", "Full name")}
          {field("phone", "Phone")}
          {field("email", "Email")}
          {field("address", "Address")}
          {field("date_of_birth", "Date of birth", { type: "date" })}
          {field("ni_number", "NI number")}
          {field("start_date", "Start date", { type: "date" })}
          <div className="flex items-center gap-3 pt-6">
            <Switch data-testid="hr-field-is_active" checked={form.is_active !== false} onCheckedChange={(v) => set("is_active", v)} />
            <span className="text-sm text-slate-700">{form.is_active !== false ? "Active" : "Inactive"}</span>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch data-testid="hr-field-uses_company_vehicle" checked={!!form.uses_company_vehicle} onCheckedChange={(v) => setForm((f) => ({ ...f, uses_company_vehicle: v, ...(v ? { uses_own_vehicle: false } : {}) }))} />
            <span className="text-sm text-slate-700">Company vehicle</span>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch data-testid="hr-field-uses_own_vehicle" checked={!!form.uses_own_vehicle} onCheckedChange={(v) => setForm((f) => ({ ...f, uses_own_vehicle: v, ...(v ? { uses_company_vehicle: false } : {}) }))} />
            <span className="text-sm text-slate-700">Owner driver (own vehicle)</span>
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-xs text-slate-600">Driver notes</Label>
          <Textarea data-testid="hr-field-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 rounded-sm border-slate-300" rows={2} />
        </div>
      </Card>

      <LicenceChecks driver={form} onChange={refreshFromChecks} />

      {form.uses_company_vehicle && (
        <div data-testid="hr-cv-docs">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Company vehicle documents</div>
          <div className="space-y-3">
            {docTypes.filter((t) => t.company_vehicle).map((t) => <DocBox key={t.id} driverId={id} type={t} stats={form.doc_stats_by_type?.[t.id]} onChange={refreshStats} required />)}
          </div>
        </div>
      )}

      {form.uses_own_vehicle && (
        <div data-testid="hr-ov-docs">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Owner driver documents</div>
          <div className="space-y-3">
            {docTypes.filter((t) => t.own_vehicle).map((t) => <DocBox key={t.id} driverId={id} type={t} stats={form.doc_stats_by_type?.[t.id]} onChange={refreshStats} required />)}
          </div>
        </div>
      )}

      {!form.uses_company_vehicle && !form.uses_own_vehicle && (
        <div data-testid="hr-general-docs">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Documents</div>
          <div className="space-y-3">
            {docTypes.filter((t) => !t.cv_only && !t.ov_only).map((t) => <DocBox key={t.id} driverId={id} type={t} stats={form.doc_stats_by_type?.[t.id]} onChange={refreshStats} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function DocBox({ driverId, type, stats, onChange, required }) {
  const [open, setOpen] = useState(false);
  const s = stats || { docs: 0, expired: 0, expiring: 0 };
  return (
    <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden" data-testid={`hr-doc-box-${type.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`hr-doc-toggle-${type.id}`} aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-slate-50 transition-colors">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-slate-900 text-white shrink-0"><FileText className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900">{type.name}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{type.hint}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0" data-testid={`hr-doc-stats-${type.id}`}>
          {required && !s.docs && <span data-testid={`hr-doc-missing-${type.id}`} className="text-[10px] uppercase tracking-wider font-semibold border rounded-sm px-1.5 py-0.5 border-red-300 bg-red-50 text-red-700">Missing</span>}
          {s.expired > 0 && <span className="text-[10px] uppercase tracking-wider font-semibold border rounded-sm px-1.5 py-0.5 border-red-300 bg-red-50 text-red-700">{s.expired} expired</span>}
          {s.expiring > 0 && <span className="text-[10px] uppercase tracking-wider font-semibold border rounded-sm px-1.5 py-0.5 border-amber-300 bg-amber-50 text-amber-800">{s.expiring} expiring</span>}
          <span className={`text-xs ${s.docs ? "text-slate-700 font-medium" : "text-slate-400"}`}>{s.docs ? `${s.docs} file${s.docs === 1 ? "" : "s"}` : "No files"}</span>
          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100">
          <FileUploader entityType="hr_driver" entityId={`${driverId}__${type.id}`} label="Files — tick 'Has expiry' to set a date" showExpiry showDocNumber onChange={onChange} />
        </div>
      )}
    </Card>
  );
}
